import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, Cost, FullProject } from 'entities';

import { Column, Row, Button, LabeledTextarea, Card } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { ApproveRejectButtons } from 'components/Buttons';
import { SendIcon } from 'components/Icons';
import { VariableCostInput } from 'components/Inputs';

import { DemographicsForm, defaultDemographics } from 'pages/Projects/components';

import { CreatedBy, ProjectLink } from '../components';

import { useToggle } from 'hooks';
import { useToast } from 'providers/Toast';

import { getFirst } from 'utils/array';
import { omitUndefined } from 'utils/object';
import { MESSAGES } from '../utils';
import { verifyFilling } from './utils';
import { COUNTRIES } from 'utils/countries';

interface SingleCountryProps {
  project: FullProject;
  removeSelf: (projectId: string) => void;
}

export function SingleCountry({ project, removeSelf }: SingleCountryProps) {
  const { isOn: allow, toggle } = useToggle();

  const [comment, setComment] = useState('');

  const { id, name, owner, services, addCountryRequest } = project;

  const country = addCountryRequest?.country as string;

  const { addToast } = useToast();

  const { t } = useLang();

  const countryServices: Array<{ id: string; name: string; costs: Cost[] }> = useMemo(
    () =>
      services
        .filter(({ id }) => addCountryRequest?.servicesToInclude.includes(id))
        .map(({ id, name, costs }) => {
          return {
            id,
            name,
            costs: getFirst(Object.values(costs)),
          };
        }),
    [services, addCountryRequest],
  );

  const defaultCosts = useMemo(
    () =>
      Object.fromEntries(
        countryServices.map(({ costs, id }) => [
          id,
          costs.map((cost) =>
            omitUndefined({
              ...cost,
              currency: cost.type === 'CURRENCY' ? '' : undefined,
              value: '',
            }),
          ),
        ]),
      ) as Record<string, Cost[]>,
    [countryServices],
  );

  const [countryCosts, setCountryCosts] = useState<Record<string, Cost[]>>(defaultCosts);
  const [demographics, setDemographics] = useState(defaultDemographics);

  const missingFilling = allow && verifyFilling(countryCosts, demographics);

  const sendAnswer = actions.projects.answerProjectCountryAddition.use({
    block: !allow && !comment,

    onSuccess: () => removeSelf(id),
  });

  const handleSendReview = useCallback(async () => {
    if (!allow && !comment) return addToast(MESSAGES.addCountryCommentOnReject(t));

    const extraParams = allow ? { demographics, costs: countryCosts } : {};

    await sendAnswer.execute({
      project: id,
      comment,
      allow,
      ...extraParams,
    });
  }, [allow, addToast, t, countryCosts, demographics, id, comment, sendAnswer]);

  const handleCostChange = ({
    target,
  }: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = target;

    const [serviceId, index] = name.split('#');

    setCountryCosts((old) => ({
      ...old,

      [serviceId]: old[serviceId].map((cost, costIndex) =>
        costIndex !== Number(index)
          ? cost
          : {
              ...cost,
              value: value as string,
              ...(cost.type === 'CURRENCY' ? { currency: project.currency } : {}),
            },
      ),
    }));
  };

  const handleDemographicsChange = (name: string, value: string | boolean): void => {
    setDemographics((old) => ({
      ...old,
      [name]: value,
    }));
  };

  const handleToggle = (): void => {
    toggle();

    setCountryCosts(defaultCosts);
    setDemographics(defaultDemographics);
  };

  return (
    <Card flow="column" gap="6">
      <Row justifyContent="space-between">
        <Row gap="2">
          <CreatedBy id={project.createdBy} />

          <ProjectLink id={id} text={name} />
        </Row>

        <UserAvatar user={owner} name nameClassName="font-medium" nameSide="left" />
      </Row>

      <Column>
        <Row justifyContent="space-between">
          <Row>
            <p className="font-medium">{t('country-requested')}:</p>

            <div className="rounded-lg bg-primary px-2 py-1 font-semibold text-primary-foreground">
              {COUNTRIES.find((c) => c.value === country)!.label}
            </div>
          </Row>

          <ApproveRejectButtons value={allow} onChange={handleToggle} />
        </Row>

        {addCountryRequest?.demographicsComment && (
          <LabeledTextarea
            label={`${t('demographics-comment')}:`}
            value={addCountryRequest.demographicsComment}
            disabled
          />
        )}

        {addCountryRequest?.justification && (
          <LabeledTextarea
            label={`${t('Justification')}:`}
            value={addCountryRequest.justification}
            disabled
          />
        )}

        {allow && (
          <>
            <Column>
              <p className="font-medium">{t('services')}:</p>

              {countryServices.map(({ id, name, costs }) => (
                <Card flow="column" key={`cost--${id}--${country}`}>
                  <p className="font-medium text-primary">{name}</p>

                  <div className="grid grid-cols-auto-250 gap-4">
                    {costs.map((cost, index) => (
                      <VariableCostInput
                        key={`cost-${country}-${cost.name}`}
                        type={cost.type}
                        label={cost.name}
                        currency={project.currency}
                        currencyDisabled
                        onChange={handleCostChange}
                        value={countryCosts[id][index].value || ''}
                        name={`${id}#${index}`}
                      />
                    ))}
                  </div>
                </Card>
              ))}
            </Column>

            <Column>
              <p className="font-medium">{t('demographics')}:</p>

              <DemographicsForm
                demographics={demographics}
                handleChange={handleDemographicsChange}
              />
            </Column>
          </>
        )}

        <Column gap="6">
          <LabeledTextarea
            label={`${t('add-any-comments')}:`}
            value={comment}
            onChange={({ target }) => setComment(target.value)}
          />

          <Button onClick={handleSendReview} disabled={missingFilling} loading={sendAnswer.loading}>
            <SendIcon />
            {t('send-review')}
          </Button>
        </Column>
      </Column>
    </Card>
  );
}
